import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";

import { Box, Typography, Divider, Grid2, Checkbox, Button } from "@mui/material";
import SearchInput from "../../Overview/components/SearchInput";
import {
  GroupByButton,
  DropdownMenu,
  GroupByMenuItem,
} from "../../BmsFaults/Components/StyledComponents";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { useFleetBreakdownData } from "../../FleetPerformance/Hooks/useFleetBreakdownData";
import { getSOHBreakdownGroupByData, getRULBreakdownChartData } from "../../../Api/BatteryHealthApi";
import SOHBreakdownBarChart from "../../../Charts/Bar/SOHBreakdownBarChart";
import RULBreakdownDrillDownModal from "./RULBreakdown/RULBreakdownDrillDownModal";
import { useOrgLabels } from "../../../Hooks/usePagesContent";
import { applyDimensionLabels } from "../../../Helper/Analytics/breakdownDimensions";
import { useRULBuckets } from "./utils/rulBuckets";
import { RUL_BUCKET_KEYS } from "./utils/rulConstants";

const RUL_BAR_COLOR = "#74B8C4";

const GROUP_BY_OPTIONS = [
  { value: "originCity",  label: "By City",            apiKey: "originCity"  },
  { value: "originState", label: "By State",           apiKey: "originState" },
  { value: "originDepot", label: "By Depot",           apiKey: "originDepo"  },
  { value: "mfgYear",     label: "By Onboarding Year", apiKey: "mfgYear"     },
  // The two customer dimensions are renamed at render time from globalConfig.labels.
  { value: "fleetName",     label: "By Customer Account", apiKey: "fleetName"     },
  { value: "fleetCategory", label: "By Business Segment", apiKey: "fleetCategory" },
];

const GROUP_BY_API_KEY = Object.fromEntries(
  GROUP_BY_OPTIONS.map((o) => [o.value, o.apiKey])
);

const AXIS_Y_TO_GROUP_BY = {
  city:              { value: "originCity",  label: "By City",          apiKey: "originCity"  },
  state:             { value: "originState", label: "By State",         apiKey: "originState" },
  depot:             { value: "originDepot", label: "By Depot",         apiKey: "originDepo"  },
  manufacturingYear: { value: "mfgYear",     label: "By Mfg. Year",    apiKey: "mfgYear"     },
  configName:        { value: "configName",  label: "By Configuration", apiKey: "configName"  },
  cellChemistry:     { value: "cellChemistry", label: "By Cell Chemistry", apiKey: "cellChemistry" },
  fleetName:         { value: "fleetName",     label: "By Customer Account", apiKey: "fleetName"     },
  fleetCategory:     { value: "fleetCategory", label: "By Business Segment", apiKey: "fleetCategory" },
};

const SIDEBAR_LIST_MAX_HEIGHT = "380px";
const ALL_UNIT_FILTERS = [
  { key: "months", label: "Months & Years" },
  { key: "cycles", label: "Cycles" },
  { key: "km",     label: "km" },
];

const RemainingUsefulLifeBreakdownChart = ({ globalFilters, chartConfig = {} }) => {
  const rulBuckets = useRULBuckets();
  const { fleetLabel, fleetCategoryLabel } = useOrgLabels();
  const unitFilters = useMemo(() => {
    const supported = chartConfig?.rulBreakdownChart?.supportedUnits;
    if (!supported || supported.length === 0) return ALL_UNIT_FILTERS;
    return ALL_UNIT_FILTERS.filter((f) => supported.includes(f.key));
  }, [chartConfig]);

  const [groupBy, setGroupBy] = useState(() => {
    const combinations = chartConfig?.rulBreakdownChart?.axisCombinations || [];
    const defaultCombo = combinations.find((c) => c.isDefault) || combinations[0];
    if (defaultCombo) {
      const option = AXIS_Y_TO_GROUP_BY[defaultCombo.y];
      if (option) return option.value;
    }
    return "originCity";
  });
  const [activeUnit, setActiveUnit] = useState(() => {
    const supported = chartConfig?.rulBreakdownChart?.supportedUnits;
    if (supported && supported.length > 0) return supported[0];
    return "months";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  const groupByOptions = useMemo(() => {
    const combinations = chartConfig?.rulBreakdownChart?.axisCombinations || [];
    const options = combinations.length === 0
      ? GROUP_BY_OPTIONS
      : combinations.map((combo) => AXIS_Y_TO_GROUP_BY[combo.y]).filter(Boolean);
    // The customer dimensions take their names from the same org meta row that names
    // them everywhere else, so renaming the customer renames these menu entries too.
    return applyDimensionLabels(options, {
      fleetName: `By ${fleetLabel}`,
      fleetCategory: `By ${fleetCategoryLabel}`,
    });
  }, [chartConfig, fleetLabel, fleetCategoryLabel]);

  const groupByApiKey = useMemo(() =>
    Object.fromEntries(groupByOptions.map((o) => [o.value, o.apiKey])),
    [groupByOptions]
  );

  const handleFetchSidebarData = useCallback(async (currentGroupBy) => {
    const apiKey = groupByApiKey[currentGroupBy] || "originCity";
    try {
      const response = await getSOHBreakdownGroupByData(globalFilters, apiKey);
      if (response?.responseStatus?.code === 200) {
        const data  = response.response?.data || [];
        const items = data.map((item) => ({
          id:    item.xLabel,
          label: item.xLabel,
          count: item.metricsArray?.find((m) => m.type === "DistinctVehicleCount")?.value ?? 0,
        }));
        if (items.length > 0) {
          const totalCount = items.reduce((s, i) => s + i.count, 0);
          return { items: [{ id: "all", label: "All", count: totalCount }, ...items] };
        }
      }
    } catch (err) {
      console.error("RULBreakdown: sidebar fetch error", err);
    }
    return { items: [] };
  }, [globalFilters, groupByApiKey]);

  const handleFetchChartData = useCallback(async (currentGroupBy, selectedItems) => {
    const filterName    = groupByApiKey[currentGroupBy] || "originCity";
    const isAllSelected = selectedItems.some((i) => i.id === "all");
    const filterValues  = isAllSelected ? [] : selectedItems.map((i) => i.id);
    const bucketKey     = RUL_BUCKET_KEYS[activeUnit];
    // Range buckets then "RUL Unavailable" (no RUL for this unit) last.
    const unitBuckets   = rulBuckets[activeUnit] ?? [];
    const bucketOrder   = unitBuckets.map((b) => b.name);
    const displayLabels = Object.fromEntries(unitBuckets.map((b) => [b.name, b.displayLabel ?? b.name]));

    const response = await getRULBreakdownChartData(globalFilters, filterName, filterValues, bucketKey);
    if (response?.responseStatus?.code !== 200) return [];

    const data     = response.response?.data ?? [];
    const countMap = Object.fromEntries(
      data.map(({ xLabel, metricsArray }) => [
        xLabel,
        metricsArray?.find((m) => m.type === "DistinctVehicleCount")?.value ?? 0,
      ])
    );
    const total = Object.values(countMap).reduce((s, v) => s + v, 0);

    return bucketOrder.map((bucket) => {
      const count = countMap[bucket] ?? 0;
      return {
        category:     bucket,                          // wire label (drill-down filter)
        displayLabel: displayLabels[bucket] ?? bucket, // UI label
        count,
        percentage: total > 0 ? parseFloat(((count / total) * 100).toFixed(2)) : 0,
      };
    });
  }, [activeUnit, globalFilters, groupByApiKey, rulBuckets]);

  const config = useMemo(() => ({
    onFetchSidebarData: handleFetchSidebarData,
    onFetchChartData: handleFetchChartData,
  }), [handleFetchSidebarData, handleFetchChartData]);

  const {
    groupByItems,
    selectedItems,
    setSelectedItems,
    chartData,
    loadingGroupBy,
    loadingChart,
  } = useFleetBreakdownData({ dependencies: [globalFilters], groupBy, config });

  const chartRows = chartData;

  const rulLabelColorMap = useMemo(
    () => Object.fromEntries((rulBuckets[activeUnit] ?? []).map((b) => [b.name, b.color])),
    [rulBuckets, activeUnit]
  );

  const clickStateRef = useRef({ selectedItems, activeUnit });
  useEffect(() => {
    clickStateRef.current = { selectedItems, activeUnit };
  }, [selectedItems, activeUnit]);

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    setSearchQuery("");
    setDropdownAnchorEl(null);
  };

  const handleItemToggle = useCallback((item) => {
    if (loadingChart) return;
    setSelectedItems((prev) => {
      const isAllSelected = prev.some((i) => i.id === "all");
      let next = [];
      if (item.id === "all") {
        next = isAllSelected ? [] : [item];
      } else if (isAllSelected) {
        next = groupByItems.filter((i) => i.id !== "all" && i.id !== item.id);
      } else {
        next = prev.some((i) => i.id === item.id)
          ? prev.filter((i) => i.id !== item.id)
          : [...prev, item];
      }

      const allIndividual = groupByItems.filter((i) => i.id !== "all");
      if (next.length === allIndividual.length && !next.some((i) => i.id === "all")) {
        const allItem = groupByItems.find((i) => i.id === "all");
        if (allItem) return [allItem];
      }
      return next;
    });
  }, [groupByItems, loadingChart, setSelectedItems]);

  const filteredItems = groupByItems.filter((item) =>
    item.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBarClick = useCallback((row) => {
    const { selectedItems: current, activeUnit: currentUnit } = clickStateRef.current;
    const isAllSelected = current.some((i) => i.id === "all");
    const locationLabel  = isAllSelected ? "All" : current.map((i) => i.label).join(", ");
    const locationFilter = isAllSelected
      ? null
      : { name: groupByApiKey[groupBy], value: current.map((i) => i.id) };
    setSelectedDataPoint({
      locationLabel,
      locationFilter,
      rulCategory: row.category,
      rulCategoryLabel: row.displayLabel ?? row.category,
      rulUnit: currentUnit,
    });
    setModalOpen(true);
  }, [groupBy, groupByApiKey]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDataPoint(null);
  }, []);

  const selectedOption = groupByOptions.find((o) => o.value === groupBy);


  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          maxWidth: "calc(100vw - 102px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "28px",
            py: "24px",
          }}
        >
          <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
            Remaining Useful Life Breakdown
          </Typography>
        </Box>

        <Box sx={{ display: "flex", overflowX: "auto" }}>
          <Box
            sx={{
              width: "340px",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              mb: 2,
              padding: "0px 0px 30px 0px"
            }}
          >
            <Box sx={{ padding: "0 14px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <GroupByButton
                  onClick={(e) => setDropdownAnchorEl(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon sx={{ color: "#5E758D", fontSize: "10px" }} />}
                  startIcon={<GridViewRoundedIcon sx={{ color: "#5E758D", fontSize: "12px" }} />}
                >
                  {selectedOption?.label}
                </GroupByButton>
                <Typography variant="fw400s12c686868">NO. OF VEHICLES</Typography>
              </Box>

              <Divider sx={{ mb: 1, borderColor: "#E4E4E4" }} />

              <Box sx={{ mb: 1 }}>
                <SearchInput
                  placeholder={`Search ${selectedOption?.label?.replace("By ", "") || "City"}`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Box>

              <DropdownMenu
                anchorEl={dropdownAnchorEl}
                open={Boolean(dropdownAnchorEl)}
                onClose={() => setDropdownAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                {groupByOptions.map((option) => (
                  <GroupByMenuItem
                    key={option.value}
                    selected={groupBy === option.value}
                    onClick={() => handleGroupByChange(option.value)}
                  >
                    {option.label}
                  </GroupByMenuItem>
                ))}
              </DropdownMenu>
            </Box>

            <Box sx={{ maxHeight: SIDEBAR_LIST_MAX_HEIGHT, overflowY: "auto", padding: "0px 20px 0px 28px" }}>
              {loadingGroupBy ? (
                <Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
                  <CartLoader />
                </Box>
              ) : filteredItems.length === 0 ? (
                <Box sx={{ padding: "16px", textAlign: "center" }}>
                  <Typography variant="fw400s14c686868">No items found</Typography>
                </Box>
              ) : (
                <Grid2 container gap={0.5} flexDirection="column">
                  {filteredItems.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => handleItemToggle(item)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 0",
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 },
                        transition: "opacity 0.2s",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Checkbox
                          checked={
                            selectedItems.some((i) => i.id === "all") ||
                            selectedItems.some((i) => i.id === item.id)
                          }
                          indeterminate={
                            item.id === "all" &&
                            !selectedItems.some((i) => i.id === "all") &&
                            selectedItems.length > 0
                          }
                          sx={{
                            padding: 0,
                            color: "#6D72F6",
                            "&.Mui-checked": { color: "#6D72F6" },
                            "&.MuiCheckbox-indeterminate": { color: "#6D72F6" },
                            "& .MuiSvgIcon-root": { fontSize: "20px" },
                          }}
                        />
                        <Typography
                          variant="fw400s14c000000"
                          sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="fw600s14c000000"
                        sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                      >
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                </Grid2>
              )}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", mb: 6, ml: "0px" }} />

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              padding: "0px 28px 24px 28px",
              display: "flex",
              flexDirection: "column",
              gap: 5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {unitFilters.map((item) => {
                const selected = item.key === activeUnit;
                return (
                  <Button
                    key={item.key}
                    disableRipple
                    onClick={() => setActiveUnit(item.key)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "16px",
                      px: 2,
                      py: 0.5,
                      minWidth: "auto",
                      fontSize: 14,
                      fontWeight: 500,
                      border: "1px solid",
                      borderColor: selected ? "#6D72F6" : "#D7DCE3",
                      color: selected ? "#6D72F6" : "#4B4B4B",
                      backgroundColor: selected ? "#EEF1FF" : "#FFFFFF",
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>

            {loadingChart ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 }}>
                <CartLoader />
              </Box>
            ) : selectedItems.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 }}>
                <Typography variant="fw400s14c686868">Select at least one item</Typography>
              </Box>
            ) : (
              <SOHBreakdownBarChart
                chartRows={chartRows}
                loading={false}
                onBarClick={handleBarClick}
                chartColor={RUL_BAR_COLOR}
                labelColorMap={rulLabelColorMap}
                yAxisLabel={
                  activeUnit === "cycles" ? "RUL (Cycles)"
                  : activeUnit === "km"    ? "RUL (km)"
                  : "RUL (Months & Years)"
                }
              />
            )}
          </Box>
        </Box>
      </Box>

      <RULBreakdownDrillDownModal
        open={modalOpen}
        onClose={handleModalClose}
        selectedDataPoint={selectedDataPoint}
        globalFilters={globalFilters}
      />
    </>
  );
};

export default RemainingUsefulLifeBreakdownChart;
